import { IonBackButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonImg, IonPage, IonRow, IonTitle, IonToolbar } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useQuery, useReactiveVar } from "@apollo/client";
import { communicationQuery, userQuery } from "../../apollo-client/queries";
import { idAppCustomerDeviceVar } from "../auth/login";
import { CommunicationItem, CommunicationType, FidelityCustomer, FidelitySalesPoint } from "../../apollo-client/types";
import { intlFormat, isPast } from "date-fns";
import sanitizeHtml from "sanitize-html";
import QRCode from "react-qr-code";
import { useTenant } from "../../hooks/multiTenancy";
import { image } from "ionicons/icons";
import { useAuth } from "../../hooks/auth";
import "./singleNews.css";

const SingleNews: React.FC = () => {
  const tenant = useTenant();
  const { t, i18n } = useTranslation();
  const [auth] = useAuth();
  const { data: userData } = useQuery<{ user: FidelityCustomer }>(userQuery, { skip: !auth.authenticated });
  const { idCommunication } = useParams<{ idCommunication: string }>();
  const idAppCustomerDevice = useReactiveVar(idAppCustomerDeviceVar);
  const { data: communicationsData } = useQuery<{ communications: { records: CommunicationItem[] } }>(communicationQuery, {
    variables: {
      filter: encodeURIComponent(JSON.stringify({
        id: [idCommunication],
        idAppCustomerDevice: idAppCustomerDevice !== "" ? idAppCustomerDevice : undefined
      }))
    }
  });
  const communication = communicationsData?.communications.records[0];
  const isOffSale = communication?.appPromo?.validityEndDate ? isPast(communication?.appPromo?.validityEndDate * 1000) : false;
  const salesPoints =
    (communication?.fidelitySalesPoints || [])
    .map((s) => tenant.settings?.mobileCommerceSalesPoints?.find((m) => m.id === s.idFidelitySalesPoint))
    .filter((s): s is FidelitySalesPoint => !!s);
  return (
    <IonPage className="mainSelection">
      <IonHeader>
        <IonToolbar color="secondary">
          <IonButtons slot="start">
            <IonBackButton />
          </IonButtons>
          <IonTitle>{communication?.title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="pageContainer">
        <IonCard className="news">
          <IonCardHeader>
            {communication?.image && (<IonImg className="image" src={communication.image} />)}
          </IonCardHeader>
          <IonCardContent>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(communication?.text?? "") }} ></div>
            {communication?.appPromo && (
              <div className="validity">{t("news.valid_from")} {intlFormat(communication.appPromo.validityStartDate * 1000, { year: "2-digit", month: "2-digit", day: "numeric" }, { locale: i18n.language })} {t("news.valid_to")} {intlFormat(communication.appPromo.validityEndDate * 1000, { year: "2-digit", month: "2-digit", day: "numeric" }, { locale: i18n.language })}</div>
            )}
            {communication?.communicationType == CommunicationType.DISCOUNT && !isOffSale && communication?.appPromo && userData?.user.customer.id && (
              <div className="qr">
                <div className="header">{t("news.scan_qr")}</div>
                <div className="container">
                  <QRCode
                    size={200}
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    value={JSON.stringify({
                      ver: 2,
                      promo: communication.appPromo.id,
                      dev: idAppCustomerDevice,
                      type: 1,
                      comm: communication.id,
                      cstm: userData.user.customer.id
                    })}
                    viewBox={`0 0 200 200`}
                    />
                </div>
              </div>
            )}
            {!isOffSale && salesPoints.length > 0 && (
              <div className="sales-points">
                <div className="header">{t("news.valid_for")}</div>
                {salesPoints.map((s) => (
                  <IonCard key={s.id}>
                    <IonGrid>
                      <IonRow>
                        <IonCol size="3">
                          {s.image && (
                            <IonImg className="image-sales-point" src={s.image} style={{objectFit: "cover"}}></IonImg>
                          )}
                          {!s.image && (
                            <IonIcon className="image-sales-point" icon={image} style={{width: "100%"}} />
                          )}
                        </IonCol>
                        <IonCol className="sales-point-content" size="9">
                          <IonCardHeader>
                            <IonCardTitle>{s.name}</IonCardTitle>
                          </IonCardHeader>
                          <IonCardContent>{s.street} {s.city} {s.district}</IonCardContent>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCard>
                ))}
              </div>
            )}
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );
};

export default SingleNews;
